import { Button, Modal } from '@components/common';
import { useAppDispatch } from '@hooks/useRedux';
import { regenerateUserLoginCodeThunk } from '@store/users';
import { Eye, EyeOff, RefreshCw, User2Icon } from 'lucide-react';
import React, { useState } from 'react';
import { apiSuccess } from '../../../util/notify';

interface ManagePasscodeModalProps {
  isOpen: boolean;
  userId: string;
  employeeName: string;
  hasPasscode: boolean;
  passcode: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const ManagePasscodeModal: React.FC<ManagePasscodeModalProps> = ({
  isOpen,
  userId,
  employeeName,
  hasPasscode,
  passcode,
  onClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const [showPasscode, setShowPasscode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate/Regenerate a passcode using edge function + database logic
  const handleRegenerate = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await dispatch(regenerateUserLoginCodeThunk(userId)).unwrap();
      apiSuccess(`Passcode generated successfully for ${employeeName}`);
      onSuccess();
    } catch (err) {
      setError(
        typeof err === 'string' ? err : 'Failed to regenerate passcode.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Remove passcode completely
  // const handleRemove = async () => {
  //   if (
  //     !window.confirm(
  //       `Are you sure you want to remove the login code for ${employeeName}? This user will no longer be able to log in using a fast passcode.`,
  //     )
  //   ) {
  //     return;
  //   }

  //   setIsSubmitting(true);
  //   setError(null);

  //   try {
  //     await dispatch(
  //       assignUserLoginCodeThunk({
  //         userId,
  //         loginCode: null,
  //       }),
  //     ).unwrap();
  //     apiSuccess(`Passcode removed successfully for ${employeeName}`);
  //     onSuccess();
  //     onClose();
  //   } catch (err) {
  //     setError(typeof err === 'string' ? err : 'Failed to remove passcode.');
  //   } finally {
  //     setIsSubmitting(false);
  //   }
  // };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Manage Passcode" size="sm">
      <div className="space-y-5 text-sm text-gray-900">
        <div>
          <p className="font-semibold text-black flex flex-row justify-start items-center">
            <User2Icon size={32} />
            {employeeName}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Generate or reset the secure 4-digit numeric code to allow this
            employee to sign in quickly on allowed local devices.
          </p>
        </div>

        {/* Passcode Display Box */}
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 flex flex-col items-center justify-center relative">
          {hasPasscode && passcode ? (
            <>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Active Passcode
              </p>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-mono font-bold tracking-widest text-black">
                  {showPasscode ? passcode : '•••• ••••'}
                </span>
                <button
                  type="button"
                  onClick={() => setShowPasscode(!showPasscode)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                >
                  {showPasscode ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-2">
              <span className="inline-block px-2.5 py-1 rounded bg-amber-50 text-amber-700 text-xs font-bold uppercase tracking-wider mb-1.5 border border-amber-100">
                NONE ASSIGNED
              </span>
              <p className="text-xs text-gray-500">
                Click generate below to assign a secure code automatically.
              </p>
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 font-medium">
            {error}
          </div>
        )}

        <div className="flex items-center justify-center gap-3 pt-3 border-t border-gray-100">
          <Button
            size="lg"
            onClick={handleRegenerate}
            isLoading={isSubmitting}
            className="w-full"
          >
            <RefreshCw
              size={14}
              className={isSubmitting ? 'animate-spin' : ''}
            />
            {hasPasscode ? 'Regenerate Code' : 'Generate Code'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
